import { ref, update } from "firebase/database";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import type { AspectRecord } from "../lib/character-edits.js";
import {
  type EditableScene,
  addSceneAspect,
  addSituationalAspect,
  removeSceneAspect,
  removeSituationalAspect,
} from "../lib/scene-edits.js";
import AspectFormModal from "./AspectFormModal.js";

interface Props {
  scenePath: string;
  scene: EditableScene;
}

type ModalState = null | { list: "scene" } | { list: "situational" };

export default function SceneAspectsEditor({ scenePath, scene }: Props): ReactElement {
  const { t } = useTranslation();
  const [modal, setModal] = useState<ModalState>(null);

  async function handleCreate(list: "scene" | "situational", text: string): Promise<void> {
    const { db } = getFirebase();
    const writes =
      list === "scene"
        ? addSceneAspect(scenePath, scene, { text })
        : addSituationalAspect(scenePath, scene, { text });
    await update(ref(db), writes);
  }

  async function handleRemove(list: "scene" | "situational", id: string): Promise<void> {
    if (!window.confirm(t("edit.confirm.delete-aspect"))) return;
    const { db } = getFirebase();
    const writes =
      list === "scene"
        ? removeSceneAspect(scenePath, scene, id)
        : removeSituationalAspect(scenePath, scene, id);
    await update(ref(db), writes);
  }

  function renderList(list: "scene" | "situational", aspects: AspectRecord[]): ReactElement {
    return (
      <section>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase text-muted-foreground">
            {t(`edit.scene.${list}-aspects`)}
          </h3>
          <button
            type="button"
            className="rounded border px-2 py-0.5 text-xs"
            onClick={() => setModal({ list })}
          >
            {t("edit.aspect.add")}
          </button>
        </div>
        <ul className="mt-1 space-y-1">
          {aspects.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-sm">
              <span className="flex-1">{a.text}</span>
              <button
                type="button"
                className="rounded border border-rose-400 px-2 py-0.5 text-xs text-rose-700"
                onClick={() => handleRemove(list, a.id)}
              >
                {t("edit.delete")}
              </button>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {renderList("scene", scene.sceneAspects ?? [])}
      {renderList("situational", scene.situationalAspects ?? [])}
      <AspectFormModal
        open={modal !== null}
        mode="create"
        allowedKinds={modal?.list === "scene" ? ["scene"] : ["situation"]}
        onSubmit={(values) => {
          if (modal) {
            void handleCreate(modal.list, values.text);
            setModal(null);
          }
        }}
        onCancel={() => setModal(null)}
      />
    </div>
  );
}
